/**
 * AOZONE-104 — the MCP client setup facts rendered on /user/tokens.
 *
 * Single home for every setup string so the one-time reveal panel and the
 * persistent instructions section cannot drift from each other, and so the
 * `claude mcp add` invocation that was actually verified against prod from a real
 * Claude Code client exists in exactly one place (its golden form is pinned in
 * mcpSetup.test.ts).
 */

/**
 * Streamable-HTTP MCP endpoint (infra: the `/mcp` route on the prod
 * distribution). The only absolute, environment-specific URL in this SPA — every
 * `api.ts` call is origin-relative. It is baked into user-facing docs, so a
 * second environment would need this parameterized rather than edited.
 */
export const MCP_ENDPOINT_URL = 'https://ozone.qumulo.sh/mcp';
/** The server name to register the endpoint under, and how `/mcp` lists it. */
export const MCP_SERVER_NAME = 'ozone';
/**
 * MCP transport; the server is stateless Streamable HTTP, not stdio or SSE.
 * Doubles as the CLI's `--transport` value and the JSON config's `type` value —
 * two independent surfaces that happen to spell it the same way today. If either
 * side renames its value, split this constant instead of editing it.
 */
export const MCP_TRANSPORT = 'http';
/** The single tool the server exposes. */
export const MCP_TOOL_NAME = 'create_entry';
/** Stand-in for a token in the copy that is rendered before anything is minted. */
export const MCP_TOKEN_PLACEHOLDER = '<TOKEN>';
/**
 * Default PAT lifetime, for copy only — the reveal panel and the token table show
 * each token's real `expiresAt` from the server. Mirrors `DEFAULT_EXPIRY_DAYS` in
 * `apps/admin-api/src/routes/pat.ts`, which is authoritative; nothing keeps the
 * two in sync, so change them together (AOZONE-104).
 */
export const MCP_TOKEN_LIFETIME_DAYS = 90;

/**
 * The verified `claude mcp add` invocation, with `token` interpolated into the
 * Authorization header. Line continuations are part of the verified form and are
 * kept so the command can be pasted as-is.
 */
export function buildClaudeMcpAddCommand(token: string = MCP_TOKEN_PLACEHOLDER): string {
  return [
    `claude mcp add --transport ${MCP_TRANSPORT} --scope user ${MCP_SERVER_NAME} \\`,
    `  ${MCP_ENDPOINT_URL} \\`,
    `  --header "Authorization: Bearer ${token}"`,
  ].join('\n');
}

/** The equivalent hand-written client config. Serialized, never hand-indented. */
export function buildMcpJsonSnippet(token: string = MCP_TOKEN_PLACEHOLDER): string {
  return JSON.stringify(
    {
      mcpServers: {
        [MCP_SERVER_NAME]: {
          type: MCP_TRANSPORT,
          url: MCP_ENDPOINT_URL,
          headers: { Authorization: `Bearer ${token}` },
        },
      },
    },
    null,
    2,
  );
}
